import { SignInErrorPage } from '@/auth/components/SignInErrorPage';
import { client } from '@/client';
import {
  PENDING_LOGIN_EVENT_KEY,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { LoadingPage } from '@/pages';
import { revokeHubspotCookies } from '@/utils/hubspot';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useSearchParams } from 'react-router-dom';

const RECOVERABLE_SSO_ERROR_CODES = [
  'login_required',
  'interaction_required',
  'temporarily_unavailable',
];
const RETRYABLE_SSO_ERROR_CODES = ['invalid_grant'];

const OIDC_ERROR_RESPONSE_NAME = 'ErrorResponse';

const SSO_RETRY_ATTEMPTS_KEY = 'sso_retry_attempts';
const SSO_RETRY_LIMIT = 2;

const SSO_PKCE_RETRY_KEY = 'sso_pkce_retry';

const readSsoRetryAttempts = (): number =>
  Number(sessionStorage.getItem(SSO_RETRY_ATTEMPTS_KEY)) || 0;

const hasPkceRetried = (): boolean =>
  sessionStorage.getItem(SSO_PKCE_RETRY_KEY) === '1';

const getOidcErrorCode = (error: unknown): string => {
  if (typeof error !== 'object' || error === null) return '';
  const { error: code } = error as { error?: string };
  return code ?? '';
};

const getOidcErrorName = (error: unknown): string =>
  error instanceof Error ? error.name : '';

const isRecoverableSsoError = (code: string | null): boolean =>
  !!code && RECOVERABLE_SSO_ERROR_CODES.includes(code);

const isIdentityProviderRefusal = (error: unknown): boolean =>
  error instanceof Error && error.name === OIDC_ERROR_RESPONSE_NAME;

const clearAuthParamsFromUrl = (): void =>
  window.history.replaceState({}, document.title, window.location.pathname);

const CompleteSignIn: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [authError, setAuthError] = useState<string | null>(null);
  const hasCompletedSignIn = useRef(false);
  const hasStartedInteractiveSignIn = useRef(false);

  const urlErrorCode = searchParams.get('error');

  const isClientSideCallbackFailure = useMemo(
    () =>
      !urlErrorCode && !!auth.error && !isIdentityProviderRefusal(auth.error),
    [urlErrorCode, auth.error],
  );

  const hasSsoError = useMemo(
    () => !!searchParams.get('error') || !!auth.error,
    [searchParams, auth.error],
  );

  const ssoErrorCode = useMemo(() => {
    const urlError = searchParams.get('error');
    if (urlError) return urlError;
    if (auth.error) return getOidcErrorCode(auth.error) || null;
    return null;
  }, [searchParams, auth.error]);

  const ssoErrorMessage = useMemo(() => {
    const urlError = searchParams.get('error');
    const urlErrorDescription = searchParams.get('error_description');
    if (urlError) return urlErrorDescription || urlError;
    if (auth.error) return auth.error.message;
    return null;
  }, [searchParams, auth.error]);

  useEffect(() => {
    const startInteractiveSignIn = () => {
      if (hasStartedInteractiveSignIn.current) return;
      hasStartedInteractiveSignIn.current = true;

      if (readSsoRetryAttempts() >= SSO_RETRY_LIMIT) {
        clearAuthParamsFromUrl();
        setAuthError(ssoErrorMessage);
        Sentry.captureException(new Error('SSO sign-in retry limit reached'), {
          tags: { oidc_operation: 'ssoRetryLimit' },
          extra: { ssoErrorCode, attempts: readSsoRetryAttempts() },
        });
        return;
      }

      sessionStorage.setItem(
        SSO_RETRY_ATTEMPTS_KEY,
        String(readSsoRetryAttempts() + 1),
      );

      auth.signinRedirect().catch((error) => {
        setAuthError(error.message);
        Sentry.captureException(error, {
          tags: { oidc_operation: 'signinRedirect' },
        });
      });
    };

    if (!auth.isAuthenticated) {
      if (hasStartedInteractiveSignIn.current) return;

      if (hasSsoError) {
        if (isRecoverableSsoError(ssoErrorCode)) {
          if (auth.activeNavigator) return;

          clearAuthParamsFromUrl();

          startInteractiveSignIn();
          return;
        }

        if (
          ssoErrorCode &&
          RETRYABLE_SSO_ERROR_CODES.includes(ssoErrorCode) &&
          !hasPkceRetried()
        ) {
          sessionStorage.setItem(SSO_PKCE_RETRY_KEY, '1');

          clearAuthParamsFromUrl();

          startInteractiveSignIn();
          return;
        }

        if (isClientSideCallbackFailure) {
          if (auth.activeNavigator) return;

          clearAuthParamsFromUrl();

          Sentry.captureMessage('SSO callback failed, restarting sign-in', {
            level: 'warning',
            tags: { oidc_operation: 'ssoCallbackRetry' },
            extra: { ssoError: ssoErrorMessage },
          });

          startInteractiveSignIn();
          return;
        }

        sessionStorage.removeItem(SSO_PKCE_RETRY_KEY);

        clearAuthParamsFromUrl();

        const ssoErrorName = getOidcErrorName(auth.error);
        const ssoErrorDiscriminator = ssoErrorCode || ssoErrorName || 'unknown';

        setAuthError(ssoErrorMessage);
        Sentry.captureException(
          new Error(`SSO sign-in error: ${ssoErrorDiscriminator}`),
          {
            tags: {
              oidc_operation: 'ssoCallback',
              sso_error_code: ssoErrorCode ?? undefined,
              sso_error_name: ssoErrorName || undefined,
            },
            extra: { ssoError: ssoErrorMessage },
            fingerprint: ['sso-sign-in-error', ssoErrorDiscriminator],
          },
        );
        return;
      }

      if (auth.isLoading || auth.activeNavigator) return;

      auth.signinSilent().catch((error) => {
        if (isRecoverableSsoError(getOidcErrorCode(error))) {
          startInteractiveSignIn();
          return;
        }

        setAuthError(error.message);
        Sentry.captureException(error, {
          tags: { oidc_operation: 'signinSilent' },
        });
      });
      return;
    }

    if (hasCompletedSignIn.current) return;
    hasCompletedSignIn.current = true;

    sessionStorage.removeItem(SSO_RETRY_ATTEMPTS_KEY);
    sessionStorage.removeItem(SSO_PKCE_RETRY_KEY);

    revokeHubspotCookies();

    clearAuthParamsFromUrl();

    const completeSignIn = async () => {
      if (auth.user) {
        sessionStorage.setItem(PENDING_LOGIN_EVENT_KEY, '1');

        try {
          await client.default.updateLastLoginDate(auth.user);
        } catch (e) {
          // Login tracking is invisible to the user, so a failure here must not
          // surface as an error toast on an otherwise successful sign-in. It is
          // reported to Sentry instead, and sign-in continues.
          Sentry.captureException(e, {
            tags: { oidc_operation: 'loginTracking' },
          });
        }
      }

      navigate('/', { replace: true });
    };

    completeSignIn().catch((error) => {
      Sentry.captureException(error, {
        tags: { oidc_operation: 'completeSignIn' },
      });
    });
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    auth.activeNavigator,
    auth.error,
    hasSsoError,
    ssoErrorCode,
    ssoErrorMessage,
    isClientSideCallbackFailure,
  ]);

  if (authError) {
    return (
      <SignInErrorPage
        errorCode={ssoErrorCode}
        onTryAgain={() => navigate(RedirectPaths[RedirectPathsEnum.LOGOUT]())}
      />
    );
  }

  return <LoadingPage message={t('auth.signIn.wait')} />;
};

export default CompleteSignIn;
